'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import Image from 'next/image';
import { Typography } from '@mui/material';
import { PortableText } from '@portabletext/react';
import { sectionSpacingCss } from '../section-spacing';
import { config } from '@/config/environment';

const Container = styled('section')`
  ${sectionSpacingCss}
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;

const Title = styled(Typography)`
  font-family: var(--font-cooper-light), sans-serif;
  font-size: 50px;
  line-height: 109.3%;
  color: #ffffff;
  text-align: center;
  margin: 0 auto 20px;
  width: 100%;
  max-width: 1100px;
  @media (max-width: 768px) {
    text-align: center;
    margin-bottom: 32px;
    line-height: 120%;
  }
`;

const TabsEllipse = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  border-radius: 9999px;
  background-color: rgba(198, 153, 187, 0.35);
  border: 3px solid rgba(255, 255, 255, 0.1);
  min-width: 300px;
  @media (max-width: 480px) {
    max-width: 90vw;
  }
  /* two subtle radial gradients with 20% and 10% opacity */
  background-image: radial-gradient(circle at 20% 50%, rgba(198, 153, 187, 0.2), transparent 40%),
    radial-gradient(circle at 80% 50%, rgba(198, 153, 187, 0.1), transparent 60%);
`;
const TabButton = styled('button')<{ active?: boolean }>`
  background: ${props => (props.active ? '#FFCF31' : 'transparent')};
  border: none;
  padding: 8px;
  margin: 0px;
  border-radius: 999px;
  min-width: 200px;
  min-height: 40px;
  @media (max-width: 480px) {
    min-width: 150px;
  }
  cursor: pointer;
  font-family: var(--font-work-sans), sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: ${props => (props.active ? '#372240' : 'white')}; /* dark text that reads on yellow */
  transition:
    transform 200ms ease,
    box-shadow 200ms ease;
  box-shadow: ${props => (props.active ? '0 6px 18px rgba(0,0,0,0.18)' : 'none')};

  &:hover {
    transform: translateY(-3px);
  }
`;

const CardsRow = styled('div')`
  display: flex;
  gap: 24px;
  align-items: flex-start;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 24px;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const CardsContainer = styled('div')<{ flipping: boolean }>`
  width: 100%;
  display: flex;
  justify-content: center;
  /* animate both transform and opacity so the element fades while it moves */
  transition:
    transform 360ms cubic-bezier(0.2, 0.9, 0.2, 1),
    opacity 365ms cubic-bezier(0.2, 0.9, 0.2, 1);
  transform-origin: center;
  transform-style: preserve-3d;
  perspective: 1000px;
  /* fade out when flipping, fade in when not */
  opacity: ${p => (p.flipping ? 0 : 1)};
  /* use a subtle rotate + translate so the motion pairs with the fade */
  transform: ${p => (p.flipping ? 'rotateY(12deg) translateY(6px) scale(0.995)' : 'none')};
  backface-visibility: hidden;
  will-change: transform, opacity;
`;

const Card = styled('div')<{ expanded: boolean }>`
  width: 472px;
  height: 400px;

  border: 1px solid #fff3dc;
  border-radius: 0px;
  background-color: #120214; /* prevents black flash on flip */
  box-sizing: border-box;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  contain: paint;
  perspective: 1200px;
  transition:
    transform 420ms cubic-bezier(0.22, 0.9, 0.3, 1),
    box-shadow 300ms ease;
  /* Subtle motion instead of 3D flip to avoid hit-testing issues */
  transform: ${p => (p.expanded ? 'translateY(-2px) scale(1.002)' : 'none')};
  box-shadow: ${p => (p.expanded ? '0 20px 40px rgba(0,0,0,0.5)' : '0 8px 18px rgba(0,0,0,0.3)')};
  /* establish stacking context for image/content/button */
  isolation: isolate;
  will-change: transform;
  @media (max-width: 768px) {
    width: 350px;
    max-width: 90vw;
    margin: 0 auto;
    transform: none;
  }
`;

/*const FlipVisual = styled('div')<{ mode: 'open' | 'close' }>`
  position: absolute;
  inset: 0;
  z-index: 2; /* above image, below button 
  pointer-events: none;
  background: rgba(18, 2, 20, 0.25);
  transform-origin: center;
  animation: ${p => (p.mode === 'open' ? flipOpen : flipClose)} 420ms ease;
`;*/

const CardImageWrap = styled('div')<{ expanded?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  transition:
    opacity 320ms cubic-bezier(0.2, 0.9, 0.2, 1),
    transform 320ms cubic-bezier(0.2, 0.9, 0.2, 1);
  opacity: ${p => (p.expanded ? 0 : 1)};
  transform: ${p =>
    p.expanded ? 'scale(1.02) translateY(-6px) translateZ(0)' : 'scale(1) translateZ(0)'};
  pointer-events: none; /* image shouldn't capture pointer events over button */
  background-color: #120214;
  @media (max-width: 768px) {
    transform: ${p => (p.expanded ? 'scale(1) translateY(0)' : 'scale(1)')};
  }
`;

const CardContent = styled('div')<{ expanded: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 92px; /* keep content above the floating button */
  padding: 16px 20px 120px;
  color: ${p => (p.expanded ? '#ffffff' : 'transparent')};
  opacity: ${p => (p.expanded ? 1 : 0)};
  transition:
    opacity 320ms cubic-bezier(0.2, 0.9, 0.2, 1),
    color 320ms ease,
    transform 320ms cubic-bezier(0.2, 0.9, 0.2, 1);
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  background: rgba(15, 4, 20, 0.96);
  z-index: 2; /* sit above the image when flipped */
  pointer-events: ${p => (p.expanded ? 'auto' : 'none')};
  user-select: ${p => (p.expanded ? 'text' : 'none')};
  @media (max-width: 768px) {
    transform: none;
    bottom: 118px;
    padding-bottom: 140px;
  }
`;

const FlipInner = styled('div')<{ expanded: boolean }>`
  position: absolute;
  inset: 0;
  transform-style: preserve-3d;
  transition: transform 520ms cubic-bezier(0.22, 0.9, 0.3, 1);
  transform: ${p => (p.expanded ? 'rotateY(180deg)' : 'rotateY(0deg)')};
  @media (max-width: 768px) {
    transform: none;
  }
`;

const Face = styled('div')`
  position: absolute;
  inset: 0;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  transform-style: preserve-3d;
`;

const FrontFace = styled(Face)<{ expanded: boolean }>`
  transform: rotateY(0deg);
  pointer-events: ${p => (p.expanded ? 'none' : 'auto')};
  @media (max-width: 768px) {
    transform: none;
  }
`;

const BackFace = styled(Face)<{ expanded: boolean }>`
  transform: rotateY(180deg);
  pointer-events: ${p => (p.expanded ? 'auto' : 'none')};
  @media (max-width: 768px) {
    transform: none;
  }
`;

const BigEllipseButton = styled('button')<{ expanded: boolean }>`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 18px;
  width: calc(100% - 48px);
  height: 56px;
  border-radius: 999px;
  background: ${p => (p.expanded ? '#ffcf31' : '#fff3dc')};
  :hover {
    background: #ffcf31;
  }
  border: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 18px 0 20px;
  cursor: pointer;
  font-weight: 700;
  color: #472b4a;
  transition:
    transform 220ms ease,
    box-shadow 220ms ease;
  z-index: 3; /* float above image and content */

  .label {
    text-align: left;
    font-size: 16px;
  }

  .sign {
    font-size: 20px;
    line-height: 1;
    margin-left: 12px;
  }

  /* On mobile we disable the card flip; avoid mirroring button text. */
  @media (max-width: 768px) {
    transform: translateX(-50%);
  }
`;

const LinkRow = styled('div')`
  margin-top: auto;
  text-align: left;
  position: relative;
  z-index: 4; /* sit above the floating button */
  /* leave space below the link so the button doesn't overlap it */
  margin-bottom: 100px;
  @media (max-width: 768px) {
    margin-bottom: 120px;
  }
`;

const BottomEllipseLink = styled('a')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 28px;
  padding: 14px 28px;
  border-radius: 999px;
  background: transparent; /* transparent background per request */
  color: #ffcf31;
  border: 2px solid #ffcf31;
  text-decoration: none;
  font-family:
    work sans,
    sans-serif;
  font-weight: 700;
  font-size: 20px;
  cursor: pointer;
  transition:
    transform 180ms ease,
    box-shadow 180ms ease;
  z-index: 5;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(255, 207, 49, 0.12);
    background: #ffcf31;
    color: #231727;
    border-color: #ffcf31;
  }
`;

const portableTextComponents = {
  marks: {
    link: ({ children, value }: any) => {
      const target = (value?.href || '').startsWith('http') ? '_blank' : undefined;
      return (
        <a
          href={value?.href}
          target={target}
          rel={target === '_blank' ? 'noreferrer noopener' : undefined}
          style={{
            color: '#ffffff',
            textDecoration: 'underline',
            cursor: 'pointer',
            position: 'relative',
            zIndex: 1000,
            pointerEvents: 'all',
          }}
          onClick={e => e.stopPropagation()}
        >
          {children}
        </a>
      );
    },
  },
};

type CardItemProps = {
  title: string;
  description?: any;
  linkText?: string;
  image: string;
  keyId: string;
  collapsedLabel: string;
  linkHref?: string;
  isOpen: boolean;
  onToggle: (keyId: string) => void;
};

const CardItem = React.memo(function CardItem({
  title,
  description,
  image,
  keyId,
  collapsedLabel,
  linkHref,
  linkText,
  isOpen,
  onToggle,
}: CardItemProps) {
  const handleToggle = useCallback(() => onToggle(keyId), [onToggle, keyId]);

  return (
    <Card expanded={isOpen} aria-expanded={isOpen}>
      <FlipInner expanded={isOpen}>
        <FrontFace expanded={isOpen}>
          <CardImageWrap expanded={isOpen}>
            <Image src={image} alt={title} fill style={{ objectFit: 'cover' }} priority={false} />
          </CardImageWrap>
        </FrontFace>

        <BackFace expanded={isOpen}>
          <CardContent expanded={isOpen}>
            <Typography
              variant="body2"
              component="div"
              sx={{
                fontSize: '18px',
                fontFamily: 'var(--font-work-sans), sans-serif',
                fontWeight: 400,
                lineHeight: 1.5,
                color: '#ffffff',
                opacity: 0.95,
                '@media (max-width: 768px)': {
                  fontSize: '16px',
                },
                '& p': {
                  margin: 0,
                  marginBottom: '1em',
                },
                '& p:last-child': {
                  marginBottom: 0,
                },
              }}
            >
              {isOpen ? (
                typeof description === 'string' ? (
                  description
                ) : (
                  <PortableText value={description} components={portableTextComponents} />
                )
              ) : (
                'Test'
              )}
            </Typography>

            {isOpen && linkText && config.platformMode !== 'archive' && (
              <LinkRow>
                <a
                  href={linkHref || '#'}
                  style={{
                    color: '#FFCF31',
                    fontSize: '18px',
                    fontFamily: 'var(--font-work-sans), sans-serif',
                    fontWeight: 400,
                  }}
                >
                  {linkText || 'See experiences'}
                </a>
              </LinkRow>
            )}
          </CardContent>
        </BackFace>
      </FlipInner>

      <BigEllipseButton
        type="button"
        expanded={isOpen}
        aria-controls={`card-${keyId}`}
        aria-expanded={isOpen}
        onClick={handleToggle}
      >
        <div className="label">{collapsedLabel}</div>
        <div className="sign">{isOpen ? '−' : '+'}</div>
      </BigEllipseButton>
    </Card>
  );
});

export default function CoachingJourney({ data }: { data?: any }): React.ReactElement {
  const [tab, setTab] = useState<'one' | 'group'>('one');
  const [flipping, setFlipping] = useState(false);
  const timers = useRef<number[]>([]);
  const [expanded, setExpanded] = useState<{ [k: string]: boolean }>({
    oneoff: false,
    multisession: false,
    grouponeoff: false,
    cohort: false,
  });

  const toggle = useCallback((key: string) => {
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleTabChange = (newTab: 'one' | 'group') => {
    if (newTab === tab || flipping) return;
    // start flip
    setFlipping(true);
    // at halfway, swap content
    const swap = window.setTimeout(() => setTab(newTab), 180);
    // end flip
    const end = window.setTimeout(() => setFlipping(false), 360);
    timers.current.push(swap, end);
  };

  useEffect(() => {
    return () => {
      // clear any pending timers on unmount
      timers.current.forEach(id => clearTimeout(id));
      timers.current = [];
    };
  }, []);

  // flip logic replaces the old fade animation for tab changes

  const stepsToRender = React.useMemo(() => {
    if (!data?.steps || !Array.isArray(data.steps)) return [];
    if (config.platformMode !== 'archive') return data.steps;
    return data.steps.map((s: any) => {
      if (s.stepNumber === 1)
        return {
          ...s,
          description:
            'A single session offered people an opportunity to check-in with a coach, talk through what was on their mind, and explore what mattered most.',
        };
      if (s.stepNumber === 2)
        return {
          ...s,
          description:
            'Whether online or in-person, coaching journeys helped people build some momentum towards a life full of connection.',
        };
      if (s.stepNumber === 3)
        return {
          ...s,
          description:
            'A single group session was a chance to explore a topic—like anxiety, creativity, or friendships—and meet others with different experiences. Online or in person, it was easy to fit into your schedule. The goal? To spark new ideas, gain insights, and find your next step forward.',
        };
      if (s.stepNumber === 4)
        return {
          ...s,
          description:
            'Cohorts were small groups where the same people came together each week. Over 6 or 8 weeks, Cohorts offered a chance to go on a journey with others: learning from and sharing with each other, trying new things together, and forming a bond. People could join a Cohort, dive into something new, and collaborate with others for a unique experience.',
        };
      return s;
    });
  }, [data?.steps]);

  return (
    <Container>
      <Title variant="h2">
        {config.platformMode === 'archive'
          ? 'Coaching journeys'
          : data?.heading || 'Find your Coaching journey'}
      </Title>
      <TabsEllipse>
        <TabButton
          active={tab === 'one'}
          onClick={() => handleTabChange('one')}
          aria-pressed={tab === 'one'}
        >
          One-on-one
        </TabButton>
        <TabButton
          active={tab === 'group'}
          onClick={() => handleTabChange('group')}
          aria-pressed={tab === 'group'}
        >
          Group
        </TabButton>
      </TabsEllipse>

      <CardsContainer flipping={flipping}>
        <CardsRow>
          {/* If data.provided steps exist, map them into card items; otherwise use defaults */}
          {stepsToRender.length > 0 &&
            stepsToRender
              .filter((s: any) => (tab === 'one' ? s.stepNumber < 3 : s.stepNumber >= 3))
              .map((s: any, idx: number) => (
                <CardItem
                  key={`step-${idx}`}
                  keyId={`step-${idx}`}
                  title={s.title || `Step ${idx + 1}`}
                  description={s.description}
                  image={s.iconUrl || '/discover/coaching/coaching-journey-1.png'}
                  collapsedLabel={s.title || `Step ${idx + 1}`}
                  linkHref={s.link || '#'}
                  isOpen={!!expanded[`step-${idx}`]}
                  onToggle={toggle}
                />
              ))}{' '}
          {/*: tab === 'one' ? (
            <>
              <CardItem
                key="oneoff"
                keyId="oneoff"
                title="One-off experiences"
                description="Welcome to Curiko! Join a community of people with and without disabilities and access free online and in-person experiences. Welcome to Curiko! Join a community of people with and without disabilities and access free online and in-person experiences."
                image="/discover/coaching/1-on-1-one-off.png"
                collapsedLabel="One-off experiences"
                linkHref="/discover/coaching/one-off"
                linkText="See all one-on-one Coaching experiences"
                isOpen={!!expanded.oneoff}
                onToggle={toggle}
              />
              <CardItem
                key="multisession"
                keyId="multisession"
                title="Multi-session journey"
                description="Welcome to Curiko! Join a community of people with and without disabilities and access free online and in-person experiences. Welcome to Curiko! Join a community of people with and without disabilities and access free online and in-person experiences."
                image="/discover/coaching/1-on-1-multi.png"
                collapsedLabel="Multi-session journey"
                linkHref="/discover/coaching/multi-session"
                linkText="Connect with a Coach to start a multi-session journey"
                isOpen={!!expanded.multisession}
                onToggle={toggle}
              />
            </>
          ) : (
            <>
              <CardItem
                key="grouponeoff"
                keyId="grouponeoff"
                title="One-off experiences"
                description="Welcome to Curiko! Join a community of people with and without disabilities and access free online and in-person experiences. Welcome to Curiko! Join a community of people with and without disabilities and access free online and in-person experiences."
                image="/discover/coaching/coaching-journey-1.png"
                collapsedLabel="One-off experiences"
                linkHref="/discover/coaching/group/one-off"
                linkText="See all Group Coaching experiences"
                isOpen={!!expanded.grouponeoff}
                onToggle={toggle}
              />
              <CardItem
                key="cohort"
                keyId="cohort"
                title="Coaching Cohorts"
                description="Welcome to Curiko! Join a community of people with and without disabilities and access free online and in-person experiences. Welcome to Curiko! Join a community of people with and without disabilities and access free online and in-person experiences."
                image="/discover/coaching/coaching-journey-2.png"
                collapsedLabel="Coaching Cohorts"
                linkHref="/discover/coaching/group/cohorts"
                isOpen={!!expanded.cohort}
                onToggle={toggle}
              />
            </>
          )*/}
        </CardsRow>
      </CardsContainer>

      {config.platformMode !== 'archive' && (
        <BottomEllipseLink href="/explore?tag=Coaching" aria-label="See all Coaching experiences">
          See all Coaching experiences
        </BottomEllipseLink>
      )}
    </Container>
  );
}
